import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { dateTime, num } from '../lib/format';
import { Badge, Button, Card, CardHead, Chips, Empty, Field, Input, Modal, Notice, Select, Textarea, useToast } from '../components/ui';
import { PeoplePicker, PushHistory, PushStatusCard, ResultSummary, SentNotifications, TestSender } from './PushPanels';
import type { PersonLite, SendResult } from './PushPanels';

interface Group {
  id: string;
  name: string;
  description: string | null;
  criteria: Record<string, unknown>;
  createdAt: string;
}

interface Outgoing {
  title: string;
  body: string;
  groupId?: string;
  userIds?: string[];
  scheduledAt?: string;
}

/** v2 §29 — targeted push, with the audience sized before anything is sent. */
export function NotificationsPage() {
  const [composing, setComposing] = useState(false);
  /** Filled in from a past send, for Send again. */
  const [draft, setDraft] = useState<{ title: string; body: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [lastResult, setLastResult] = useState<SendResult | null>(null);
  const [editingGroup, setEditingGroup] = useState<{ row?: Group } | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const groups = useQuery({ queryKey: ['notification-groups'], queryFn: () => api.get<Group[]>('/notifications/groups') });

  const saveGroup = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/notifications/groups', body),
    onSuccess: () => {
      toast('Group saved', 'success');
      setEditingGroup(null);
      qc.invalidateQueries({ queryKey: ['notification-groups'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const send = useMutation({
    mutationFn: async (body: Outgoing): Promise<SendResult | null> => {
      const campaign = await api.post<{ id: string }>('/notifications/campaigns', body);
      // A scheduled send goes out on its own at its time.
      if (body.scheduledAt) return null;
      return api.post<SendResult>(`/notifications/campaigns/${campaign.id}/send`);
    },
    onSuccess: (r, body) => {
      if (r) {
        toast(
          `Sent to ${r.audience} user${r.audience === 1 ? '' : 's'} · ${r.delivered} phone${r.delivered === 1 ? '' : 's'} reached`,
          'success',
        );
        setLastResult(r);
      } else if (body.scheduledAt) {
        toast(`Scheduled for ${new Date(body.scheduledAt).toLocaleString('en-IN')}`, 'success');
      }
      setComposing(false);
      qc.invalidateQueries({ queryKey: ['push-history'] });
      qc.invalidateQueries({ queryKey: ['push-status'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  return (
    <div className="stack">
      <PushStatusCard />
      {lastResult && <ResultSummary r={lastResult} />}
      <Card flush>
        <CardHead
          title="Audience groups"
          sub="Targeting rules — role, KYC status, location, activity"
          action={
            <div className="row">
              <Button size="sm" onClick={() => setEditingGroup({})}>
                + New group
              </Button>
              <Button size="sm" onClick={() => setTesting(true)}>
                Send test
              </Button>
              <Button variant="primary" size="sm" onClick={() => setComposing(true)}>
                Send notification
              </Button>
            </div>
          }
        />
        {groups.isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 100 }} />
          </div>
        ) : !groups.data?.length ? (
          <Empty
            icon="🔔"
            title="No audience groups"
            body="Without a group a notification goes to every active user. Create one to target a subset."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Criteria</th>
                  <th>Audience</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {groups.data.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <div className="cell-strong">{g.name}</div>
                      {g.description && <div className="cell-muted">{g.description}</div>}
                    </td>
                    <td className="cell-mono cell-clip">{JSON.stringify(g.criteria)}</td>
                    <td>
                      <AudienceCount groupId={g.id} />
                    </td>
                    <td className="cell-muted">{dateTime(g.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PushHistory
        onSendAgain={(v) => {
          setDraft(v);
          setComposing(true);
        }}
      />
      <SentNotifications />

      {testing && <TestSender onClose={() => setTesting(false)} />}

      {composing && (
        <Composer
          groups={groups.data ?? []}
          initial={draft}
          sending={send.isPending}
          onClose={() => {
            setComposing(false);
            setDraft(null);
          }}
          onSend={send.mutate}
        />
      )}

      {editingGroup && (
        <GroupEditor saving={saveGroup.isPending} onClose={() => setEditingGroup(null)} onSave={saveGroup.mutate} />
      )}
    </div>
  );
}

function AudienceCount({ groupId }: { groupId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['group-preview', groupId],
    queryFn: () => api.get<{ count: number }>(`/notifications/groups/${groupId}/preview`),
  });
  if (isLoading) return <span className="skeleton" style={{ display: 'inline-block', width: 40, height: 12 }} />;
  return <Badge tone="blue">{num(data?.count ?? 0)} users</Badge>;
}

/** The audience option that opens the people picker. */
const PEOPLE = '__people';

function Composer({
  groups,
  initial,
  onClose,
  onSend,
  sending,
}: {
  groups: Group[];
  initial?: { title: string; body: string } | null;
  onClose: () => void;
  onSend: (v: Outgoing) => void;
  sending: boolean;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [groupId, setGroupId] = useState('');
  const [people, setPeople] = useState<PersonLite[]>([]);
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [at, setAt] = useState('');
  const chosen = groupId === PEOPLE;
  const atTime = at ? new Date(at) : null;
  const laterOk = when === 'now' || (atTime !== null && atTime.getTime() > Date.now());
  const audienceOk = !chosen || people.length > 0;

  return (
    <Modal
      title="Send a notification"
      sub={
        when === 'now'
          ? 'This goes out immediately to every device in the audience.'
          : 'This goes out by itself at the time you pick. You can cancel it until then.'
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={sending}
            disabled={!title.trim() || !body.trim() || !laterOk || !audienceOk}
            onClick={() =>
              onSend({
                title: title.trim(),
                body: body.trim(),
                groupId: groupId && !chosen ? groupId : undefined,
                userIds: chosen ? people.map((p) => p.id) : undefined,
                scheduledAt: when === 'later' && atTime ? atTime.toISOString() : undefined,
              })
            }
          >
            {when === 'now' ? 'Send now' : 'Schedule'}
          </Button>
        </>
      }
    >
      <div className="stack">
        {!groupId && (
          <Notice tone="warn" title="No group selected">
            This will reach <strong>every active user</strong>. Choose a group to narrow it.
          </Notice>
        )}
        <Field label="Audience">
          <Select
            value={groupId}
            placeholder="Everyone"
            onChange={(e) => setGroupId(e.target.value)}
            options={[{ value: PEOPLE, label: 'Chosen people…' }, ...groups.map((g) => ({ value: g.id, label: g.name }))]}
          />
        </Field>
        {chosen && (
          <Field label={`People (${people.length})`}>
            <PeoplePicker value={people} onChange={setPeople} />
          </Field>
        )}
        <Field label="When">
          <div className="stack" style={{ gap: 'var(--s2)' }}>
            <Chips
              value={when}
              onChange={(v) => setWhen(v as 'now' | 'later')}
              options={[
                { value: 'now', label: 'Now' },
                { value: 'later', label: 'Schedule for later' },
              ]}
            />
            {when === 'later' && <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />}
          </div>
        </Field>
        <Field label="Title">
          <Input value={title} autoFocus maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Message" hint={`${body.length}/500`}>
          <Textarea value={body} maxLength={500} onChange={(e) => setBody(e.target.value)} />
        </Field>

        <div style={{ background: 'var(--bg)', padding: 'var(--s4)', borderRadius: 'var(--r-chip)' }}>
          <div className="field__label" style={{ marginBottom: 8 }}>
            Preview
          </div>
          <div className="row" style={{ background: '#fff', padding: 12, borderRadius: 12, boxShadow: 'var(--shadow-soft)' }}>
            <img src="/logo.png" alt="" style={{ width: 30, height: 30, borderRadius: 7 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{title || 'Notification title'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-muted)' }}>{body || 'Message body appears here.'}</div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function GroupEditor({
  onClose,
  onSave,
  saving,
}: {
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [role, setRole] = useState('');
  const [kycStatus, setKycStatus] = useState('');
  const [hasListings, setHasListings] = useState('');
  const [isProvider, setIsProvider] = useState('');

  const criteria: Record<string, unknown> = {};
  if (role) criteria.roles = [role];
  if (kycStatus) criteria.kycStatus = kycStatus;
  if (hasListings === 'true') criteria.hasListings = true;
  if (isProvider === 'true') criteria.isProvider = true;

  return (
    <Modal
      title="New audience group"
      sub="Criteria combine — a user must match all of them."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!name.trim()}
            onClick={() => onSave({ name: name.trim(), description: description.trim() || undefined, criteria })}
          >
            Save group
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <Input value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="Verified sellers in Hyderabad" />
        </Field>
        <Field label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="row">
          <Field label="Role">
            <Select
              value={role}
              placeholder="Any role"
              onChange={(e) => setRole(e.target.value)}
              options={[
                { value: 'CUSTOMER', label: 'Customer' },
                { value: 'SELLER', label: 'Seller' },
                { value: 'SERVICE_PROVIDER', label: 'Service provider' },
                { value: 'AGENT', label: 'Partner' },
              ]}
            />
          </Field>
          <Field label="KYC status">
            <Select
              value={kycStatus}
              placeholder="Any"
              onChange={(e) => setKycStatus(e.target.value)}
              options={[
                { value: 'VERIFIED', label: 'Verified' },
                { value: 'PENDING', label: 'Pending' },
                { value: 'NOT_STARTED', label: 'Not started' },
              ]}
            />
          </Field>
        </div>
        <div className="row">
          <Field label="Has a live listing">
            <Select
              value={hasListings}
              placeholder="Doesn't matter"
              onChange={(e) => setHasListings(e.target.value)}
              options={[{ value: 'true', label: 'Yes' }]}
            />
          </Field>
          <Field label="Is a provider">
            <Select
              value={isProvider}
              placeholder="Doesn't matter"
              onChange={(e) => setIsProvider(e.target.value)}
              options={[{ value: 'true', label: 'Yes' }]}
            />
          </Field>
        </div>
        <Field label="Stored criteria">
          <div className="cell-mono" style={{ background: 'var(--bg)', padding: 11, borderRadius: 10 }}>
            {JSON.stringify(criteria)}
          </div>
        </Field>
      </div>
    </Modal>
  );
}
